import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const LanguageSelector = ({ variant = 'default', className = '' }) => {
  const { language, setLanguage, currentLanguage, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  // Compact variant for navbar
  if (variant === 'compact') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-2 py-1.5 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-white/10"
          data-testid="language-selector-compact"
        >
          <span className="text-lg">{currentLanguage.flag}</span>
          <span className="text-sm hidden sm:inline">{currentLanguage.code.toUpperCase()}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-[#1a1a1f] border border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  language === lang.code
                    ? 'bg-teal-500/20 text-teal-400'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                }`}
                data-testid={`language-option-${lang.code}`}
              >
                <span className="text-xl">{lang.flag}</span>
                <div className="flex-1">
                  <div className="text-sm font-medium">{lang.nativeName}</div>
                  <div className="text-xs text-gray-500">{lang.name}</div>
                </div>
                {language === lang.code && (
                  <Check className="w-4 h-4 text-teal-400" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Full variant (for settings page)
  if (variant === 'full') {
    return (
      <div className={`${className}`}>
        <label className="block text-gray-300 text-sm mb-2">
          <Globe className="w-4 h-4 inline mr-2" />
          Language / Язык / Til
        </label>
        <div className="grid grid-cols-3 gap-3">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${
                language === lang.code
                  ? 'bg-teal-500/20 border-teal-500 text-teal-400'
                  : 'bg-[#1a1a1f] border-gray-700 text-gray-300 hover:border-gray-600'
              }`}
              data-testid={`language-card-${lang.code}`}
            >
              <span className="text-3xl">{lang.flag}</span>
              <div className="text-center">
                <div className="font-medium">{lang.nativeName}</div>
                <div className="text-xs text-gray-500">{lang.name}</div>
              </div>
              {language === lang.code && (
                <Check className="w-5 h-5 text-teal-400" />
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1f] border border-gray-700 rounded-lg text-gray-300 hover:border-gray-600 transition-colors"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4" />
        <span className="text-lg">{currentLanguage.flag}</span>
        <span>{currentLanguage.nativeName}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-[#1a1a1f] border border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-700">
            <span className="text-xs text-gray-500 uppercase tracking-wider">Select Language</span>
          </div>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                language === lang.code
                  ? 'bg-teal-500/20 text-teal-400'
                  : 'text-gray-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <div className="flex-1">
                <div className="font-medium">{lang.nativeName}</div>
                <div className="text-xs text-gray-500">{lang.name}</div>
              </div>
              {language === lang.code && (
                <Check className="w-4 h-4 text-teal-400" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
